import React, { useState, useEffect, useCallback, useRef } from 'react';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { Calendar, Users, Phone, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import DateRangePicker from './DateRangePicker';
import { fetchDashboardKpi } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import { CALL_TYPE_LABELS, getCallTypeOptionsForTenant } from '../config/endoTaxonomy';

const TENANT_ID = process.env.REACT_APP_TENANT_ID?.trim().toLowerCase();

const Dashboard = ({ dateRange, onDateRangeChange, callType = 'all', onCallTypeChange }) => {
  const [internalDateRange, setInternalDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  const [kpi, setKpi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const isFetching = useRef(false);

  const currentDateRange = dateRange ?? internalDateRange;
  const callTypeOptions = getCallTypeOptionsForTenant(TENANT_ID);

  const handleDateRangeChange = (range) => {
    if (onDateRangeChange) {
      onDateRangeChange(range);
      return;
    }

    setInternalDateRange(range);
  };

  const getCallTypeLabel = () => {
    if (!callType || callType === 'all') return '';
    return CALL_TYPE_LABELS[callType] ?? callType;
  };

  const loadKpi = useCallback(async (showSpinner = true) => {
    if (!currentDateRange?.from || !currentDateRange?.to) {
      return;
    }

    if (isFetching.current) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    setLoadError(null);
    isFetching.current = true;

    try {
      const params = {
        date_from: format(currentDateRange.from, 'yyyy-MM-dd'),
        date_to: format(currentDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchDashboardKpi(params);
      setKpi(data);
    } catch (error) {
      setLoadError('Не удалось загрузить KPI. Попробуйте позже.');
    } finally {
      isFetching.current = false;
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  }, [callType, currentDateRange]);

  useEffect(() => {
    loadKpi();
  }, [loadKpi]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadKpi(false);
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadKpi]);

  const callTypeLabel = getCallTypeLabel();

  const kpiCards = [
    {
      title: 'Всего звонков',
      description: 'Количество вызовов за период',
      metric: kpi?.totalCalls,
      icon: Phone,
      subtitle: callTypeLabel
    },
    {
      title: 'Средняя длительность',
      description: 'Время разговора в минутах',
      metric: kpi?.avgDuration,
      icon: Calendar,
      subtitle: callTypeLabel
    },
    {
      title: 'Конверсия в целевое действие',
      description: 'Доля достигнутых действий от применимых звонков',
      metric: kpi?.conversionRate,
      icon: TrendingUp,
      subtitle: callTypeLabel,
      suffix: '%'
    },
    {
      title: 'Активные сотрудники',
      description: 'Количество операторов с нагрузкой',
      metric: kpi?.activeEmployees,
      icon: Users,
      subtitle: callTypeLabel
    }
  ];

  const formatValue = (metric, suffix) => {
    if (!metric) return '—';
    if (metric.formatted) return metric.formatted;
    if (typeof metric.current === 'number') {
      return suffix ? `${metric.current.toFixed(1)}${suffix}` : metric.current.toLocaleString();
    }
    return metric.current ?? '—';
  };

  const formatChange = (metric) => {
    if (!metric || typeof metric.changePercent !== 'number') {
      return null;
    }
    const sign = metric.changePercent > 0 ? '+' : '';
    return `${sign}${metric.changePercent.toFixed(1)}%`;
  };

  const resolveChangeType = (metric) => {
    if (!metric || typeof metric.changePercent !== 'number') {
      return 'positive';
    }
    return metric.changePercent >= 0 ? 'positive' : 'negative';
  };

  return (
    <div className="flex-1 space-y-6 p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Центр аналитики</h1>
          <p className="text-muted-foreground mt-1">
            Оперативные метрики по контакт-центру
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={callType} onValueChange={onCallTypeChange}>
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="Направление" />
            </SelectTrigger>
            <SelectContent>
              {callTypeOptions.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateRangePicker 
            value={currentDateRange} 
            onChange={handleDateRangeChange} 
          />
        </div>
      </div>

      {loadError && (
        <Card className="border-red-200 bg-red-50 text-red-800">
          <CardHeader>
            <CardTitle className="text-base">Ошибка загрузки</CardTitle>
            <CardDescription className="text-red-600">{loadError}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpiCards.map((card, index) => {
          const Icon = card.icon;
          const metricChange = formatChange(card.metric);
          return (
            <Card key={index} className="transition-all hover:shadow-md h-full">
              <div className="flex h-full flex-col p-4 gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {card.title}
                    </CardTitle>
                    {card.subtitle && (
                      <span className="text-xs text-muted-foreground font-medium">
                        ({card.subtitle})
                      </span>
                    )}
                    <CardDescription>{card.description}</CardDescription>
                  </div>
                  <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                </div>

                <div className="flex flex-1 flex-col justify-center gap-2">
                  <div className="text-3xl font-bold leading-tight">
                    {formatValue(card.metric, card.suffix)}
                  </div>
                  {metricChange && (
                    <div className="flex items-center gap-2 text-xs">
                      <Badge
                        variant="secondary"
                        className={`${
                          resolveChangeType(card.metric) === 'positive'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}
                      >
                        {metricChange}
                      </Badge>
                      <span className="text-muted-foreground">к прошлому периоду</span>
                    </div>
                  )}
                  {isLoading && (
                    <div className="text-xs text-muted-foreground">Загрузка...</div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
